import React from 'react';
import clsx from 'clsx';
import styles from './SearchDiscoverView.module.css';

type ConversationRole = 'user' | 'assistant';

interface ConversationTurn {
  id: string;
  role: ConversationRole;
  content: string;
  timestamp: string;
}

const INITIAL_CONVERSATION: ConversationTurn[] = [
  {
    id: 'intro-agent',
    role: 'assistant',
    content:
      'Hi! I can search the BAM Supabase database for award programs that match your criteria. Let me know what genre, region, or submission window you want to explore.',
    timestamp: 'just now'
  }
];

const SearchDiscoverView: React.FC = () => {
  const [searchInput, setSearchInput] = React.useState('');
  const [conversation, setConversation] = React.useState<ConversationTurn[]>(INITIAL_CONVERSATION);

  const handleSubmit = React.useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = searchInput.trim();
    if (!trimmed) {
      return;
    }

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const userTurn: ConversationTurn = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp
    };

    const assistantTurn: ConversationTurn = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content:
        'Acknowledged. I will investigate the BAM database for matching programs. '
        + 'Reply with constraints like submission fee limits or target audience to refine the results.',
      timestamp
    };

    setConversation((prev) => [...prev, userTurn, assistantTurn]);
    setSearchInput('');
  }, [searchInput]);

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <h2 className={styles.title}>Search &amp; Discovery</h2>
        <p className={styles.subtitle}>
          Describe the author, title, or strategic goal you are researching. The BAM Copilot will query existing Supabase records to surface relevant programs.
        </p>
      </header>

      <div className={styles.contentArea}>
        <div className={styles.leftPane}>
          <form className={styles.searchForm} onSubmit={handleSubmit}>
            <div className={styles.inputRow}>
              <input
                className={styles.inputField}
                placeholder="Ask about genres, deadlines, or award profiles..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                aria-label="Search and discovery prompt"
              />
              <button type="submit" className={styles.submitButton}>
                Ask
              </button>
            </div>
          </form>

          <div className={styles.conversationPanel}>
            {conversation.map((turn) => (
              <div
                key={turn.id}
                className={clsx(
                  styles.message,
                  turn.role === 'user' ? styles.messageUser : styles.messageAssistant
                )}
              >
                <div className={styles.messageMeta}>
                  <span className={styles.messageAuthor}>{turn.role === 'user' ? 'You' : 'BAM Copilot'}</span>
                  <time className={styles.messageTimestamp}>{turn.timestamp}</time>
                </div>
                <p className={styles.messageBody}>{turn.content}</p>
              </div>
            ))}
          </div>
        </div>

        <aside className={styles.rightPane} aria-label="AI generated results preview">
          <div className={styles.resultsHeader}>
            <div className={styles.resultsHeaderRow}>
              <h3 className={styles.resultsTitle}>Proposed Results Table</h3>
              <button type="button" className={styles.fullResultsButton}>
                Full Results Card
              </button>
            </div>
            <p className={styles.resultsSubtitle}>
              The agent will assemble a table using the BAM Data Quality layout once a query is processed.
            </p>
          </div>
          <div className={styles.resultsCard}>
            <table className={styles.previewTable}>
              <thead>
                <tr>
                  <th>Program</th>
                  <th>Categories</th>
                  <th>Deadline</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={5} className={styles.previewPlaceholder}>
                    Start a conversation to populate the table. The agent will align with the Data Quality schema.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default SearchDiscoverView;
